/* PMN Landing Dinamica — JS
   - Mega menu: toggle panels, click outside to close, ESC to close
   - Chips: optional autorotate (shuffle every N days), limit count
   - Search suggest container toggle (basic, no API): show/hide based on input
   - Shipping countdown under banners: dynamic, timezone Europe/Rome, business days
   - Featured slider: show 6 products, rotate selection every X hours
*/

(() => {
  'use strict';

  // Utilities

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // ===============================
  // Featured: Vetrina + Slider (rotazione ogni N ore)
  // ===============================
  const PMN = {
    fmtMoney(cents, currency) {
      const n = (Number(cents) || 0) / 100;
      try {
        return new Intl.NumberFormat('it-IT', {
          style: 'currency',
          currency: currency || 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(n);
      } catch (_) {
        return `${n.toFixed(2)} ${currency || 'EUR'}`;
      }
    },

    moneyHTML(cents, currency) {
      const formatted = this.fmtMoney(cents, currency);
      // Split like "€74,16" => int=74 dec=16 (fallback: return full)
      const m = formatted.match(/^(.*?)(\d+[\.,]\d{2})(.*)$/);
      if (!m) return `<span class="pmn-money">${formatted}</span>`;

      const num = m[2];
      const parts = num.includes(',') ? num.split(',') : num.split('.');
      const intPart = parts[0] || '';
      const decPart = parts[1] || '00';
      const prefix = (m[1] || '').trim();
      const suffix = (m[3] || '').trim();

      return `
        <span class="pmn-money">
          <span class="pmn-moneyPrefix">${prefix}</span>
          <span class="pmn-moneyInt">${intPart}</span><span class="pmn-moneySep">,</span><span class="pmn-moneyDec">${decPart}</span>
          <span class="pmn-moneySuffix">${suffix}</span>
        </span>
      `.trim();
    },

    rotateIndex(len, hours) {
      if (!len) return 0;
      const h = Number(hours) || 2;
      const bucket = Math.floor(Date.now() / (h * 60 * 60 * 1000));
      return bucket % len;
    },

    chunkFrom(items, start, size) {
      const out = [];
      for (let i = 0; i < size; i++) out.push(items[(start + i) % items.length]);
      return out;
    }
  };

  function initFeaturedShowcase(root) {
    const wrap = qs('[data-featured-showcase]', root);
    if (!wrap) return;

    // Prevent duplicate listeners/timeouts on re-init
    if (wrap._pmnFeaturedAC) {
      try { wrap._pmnFeaturedAC.abort(); } catch (_) {}
    }
    const ac = new AbortController();
    wrap._pmnFeaturedAC = ac;

    const currency = root.getAttribute('data-currency') || 'EUR';
    const hours = Number(wrap.getAttribute('data-rotate-hours') || '2');
    const heroSize = Number(wrap.getAttribute('data-hero-size') || '4');
    const sliderSize = Number(wrap.getAttribute('data-slider-size') || '6');

    const jsonEl = qs('script[data-featured-items]', wrap);
    let items = [];
    try { items = JSON.parse(jsonEl?.textContent || '[]'); } catch (_) { items = []; }
    if (!items.length) return;

    // Base index rotates every N hours
    const start = PMN.rotateIndex(items.length, hours);
    const heroItems = PMN.chunkFrom(items, start, Math.min(heroSize, items.length));
    const sliderItems = PMN.chunkFrom(items, (start + heroSize) % items.length, Math.min(sliderSize, items.length));

    // HERO
    const hero = heroItems[0];
    const heroLink = qs('[data-hero-link]', wrap);
    const heroImg = qs('[data-hero-img]', wrap);
    const heroTitle = qs('[data-hero-title]', wrap);
    const heroVendor = qs('[data-hero-vendor]', wrap);
    const heroPrice = qs('[data-hero-price]', wrap);

    if (heroLink) heroLink.href = hero.url || '#';
    if (heroImg) {
      heroImg.src = hero.image || '';
      heroImg.alt = hero.title || '';
      heroImg.loading = 'lazy';
    }
    if (heroTitle) heroTitle.textContent = hero.title || '';
    if (heroVendor) heroVendor.textContent = hero.vendor || '';
    if (heroPrice) heroPrice.innerHTML = PMN.moneyHTML(hero.price_cents, currency);

    // MINIS (3)
    const miniCards = qsa('.pmn-miniCard', wrap);
    const minis = heroItems.slice(1, 4);

    miniCards.forEach((card, idx) => {
      const p = minis[idx];
      if (!p) {
        card.style.display = 'none';
        return;
      }
      card.style.display = '';
      card.href = p.url || '#';

      const img = qs('[data-mini-img]', card);
      const title = qs('[data-mini-title]', card);
      const price = qs('[data-mini-price]', card);

      if (img) { img.src = p.image || ''; img.alt = p.title || ''; }
      if (title) title.textContent = p.title || '';
      if (price) price.innerHTML = PMN.moneyHTML(p.price_cents, currency);
    });

    // SLIDER
    // Slider nodes (be tolerant: themes may place overflow on different elements)
    const viewport =
      qs('[data-slider-viewport]', wrap) ||
      qs('.pmn-sliderViewport', wrap) ||
      qs('.pmn-prodViewport', wrap);

    const track =
      qs('[data-slider-track]', wrap) ||
      qs('.pmn-sliderTrack', wrap) ||
      qs('.pmn-prodTrack', wrap);

    const btnPrev = qs('[data-slider-prev]', wrap);
    const btnNext = qs('[data-slider-next]', wrap);
    const dots = qs('[data-slider-dots]', wrap);
    const progress = qs('[data-slider-progress]', wrap);

    if (!track) return;

    // Decide which element is actually scrollable (sometimes it's the viewport, sometimes the track)
    const getScrollEl = () => {
      const candidates = [viewport, track, track.parentElement].filter(Boolean);
      for (const el of candidates) {
        try {
          if (el.scrollWidth > el.clientWidth + 2) return el;
        } catch (_) {}
      }
      return viewport || track;
    };

    let scrollEl = getScrollEl();

    // Rebuild slides
    track.innerHTML = sliderItems.map((p) => {
      const price = PMN.moneyHTML(p.price_cents, currency);
      const compare = (Number(p.compare_cents) > Number(p.price_cents))
        ? `<span class="pmn-compare">${PMN.fmtMoney(p.compare_cents, currency)}</span>`
        : '';

      const safeTitle = String(p.title || '').replace(/"/g, '&quot;');

      return `
        <a class="pmn-prod pmn-prodSlide" href="${p.url}">
          <div class="pmn-prodMedia">
            ${p.image ? `<img src="${p.image}" alt="${safeTitle}" loading="lazy" width="600" height="600" class="pmn-prodImg">` : ''}
          </div>
          <div class="pmn-prodBody">
            <b class="pmn-prodTitle">${p.title || ''}</b>
            <div class="pmn-small pmn-muted">${p.vendor || ''}</div>
            <div style="height:10px"></div>
            <div class="pmn-price">${price}${compare}</div>
          </div>
        </a>
      `.trim();
    }).join('');
    // Refresh scroll element after DOM changes
    scrollEl = getScrollEl();

    // Slider helpers
    const getCard = () => qs('.pmn-prodSlide', track);
    const getCardW = () => {
      const card = getCard();
      if (!card) return 320;
      const r = card.getBoundingClientRect();
      return Math.max(260, r.width || 320);
    };

    const getGap = () => {
      const cs = window.getComputedStyle(track);
      const g = parseFloat(cs.columnGap || cs.gap || '18');
      return Number.isFinite(g) ? g : 18;
    };

    const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
    const getMaxScroll = () => {
      scrollEl = getScrollEl();
      return Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    };

    const updateUI = () => {
      scrollEl = getScrollEl();
      const max = getMaxScroll();
      const x = clamp(scrollEl.scrollLeft, 0, max);

      // progress bar
      if (progress) {
        const ratio = max > 0 ? (x / max) : 0;
        progress.style.transform = `scaleX(${clamp(ratio, 0, 1)})`;
      }

      // prev/next disabled state
      if (btnPrev) btnPrev.toggleAttribute('disabled', x <= 2);
      if (btnNext) btnNext.toggleAttribute('disabled', x >= max - 2);

      // active dot
      if (dots) {
        const cardW = getCardW();
        const step = cardW + getGap();
        const perView = Math.max(1, Math.round(scrollEl.clientWidth / step));
        const total = qsa('.pmn-prodSlide', track).length;
        const pages = Math.max(1, Math.ceil(total / perView));
        const page = Math.round(x / (step * perView));

        const btns = qsa('.pmn-dot', dots);
        btns.forEach((b, i) => b.classList.toggle('is-active', i === clamp(page, 0, pages - 1)));
      }
    };

    const buildDots = () => {
      if (!dots) return;

      const total = qsa('.pmn-prodSlide', track).length;
      const cardW = getCardW();
      const step = cardW + getGap();
      const perView = Math.max(1, Math.round(getScrollEl().clientWidth / step));
      const pages = Math.max(1, Math.ceil(total / perView));

      dots.innerHTML = '';
      for (let i = 0; i < pages; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pmn-dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', `Pagina ${i + 1}`);
        b.addEventListener('click', () => {
          const left = i * perView * (step);
          const se = getScrollEl();
          se.scrollTo({ left, behavior: 'smooth' });
        }, { signal: ac.signal });
        dots.appendChild(b);
      }
    };

    const scrollByPage = (dir) => {
      const se = getScrollEl();
      const cardW = getCardW();
      const step = cardW + getGap();
      const perView = Math.max(1, Math.round(se.clientWidth / step));
      se.scrollBy({ left: dir * (perView * step), behavior: 'smooth' });
    };

    // Button events
    if (btnPrev) btnPrev.addEventListener('click', () => scrollByPage(-1), { signal: ac.signal });
    if (btnNext) btnNext.addEventListener('click', () => scrollByPage(1), { signal: ac.signal });

    // Drag to scroll (desktop) + touch support
    let isDown = false;
    let startX = 0;
    let startLeft = 0;

    const onDown = (e) => {
      isDown = true;
      boundScrollEl.classList.add('is-dragging');
      startX = (e.touches ? e.touches[0].clientX : e.clientX);
      startLeft = boundScrollEl.scrollLeft;
    };

    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX);
      const dx = startX - x;
      boundScrollEl.scrollLeft = startLeft + dx;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      boundScrollEl.classList.remove('is-dragging');
    };

    // Bind interactions to the real scroll element
    const bindScrollInteractions = () => {
      const se = getScrollEl();

      se.addEventListener('mousedown', onDown, { signal: ac.signal });
      se.addEventListener('mousemove', onMove, { signal: ac.signal });
      se.addEventListener('mouseleave', onUp, { signal: ac.signal });
      se.addEventListener('mouseup', onUp, { signal: ac.signal });

      se.addEventListener('touchstart', onDown, { passive: true, signal: ac.signal });
      se.addEventListener('touchmove', onMove, { passive: true, signal: ac.signal });
      se.addEventListener('touchend', onUp, { passive: true, signal: ac.signal });

      // Scroll updates
      se.addEventListener('scroll', () => window.requestAnimationFrame(updateUI), { passive: true, signal: ac.signal });

      return se;
    };

    const boundScrollEl = bindScrollInteractions();

    let ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(() => {
        buildDots();
        updateUI();
      });
      ro.observe(boundScrollEl);
      ro.observe(track);
      ac.signal.addEventListener('abort', () => {
        try { ro.disconnect(); } catch (_) {}
      }, { once: true });
    } else {
      window.addEventListener('resize', () => {
        buildDots();
        updateUI();
      }, { signal: ac.signal });
    }

    // Initial
    buildDots();
    updateUI();

    // Auto-refresh when the time bucket changes (no reload)
    const bucketMs = (Number.isFinite(hours) && hours > 0 ? hours : 2) * 60 * 60 * 1000;
    const now = Date.now();
    const next = (Math.floor(now / bucketMs) + 1) * bucketMs;
    const delay = Math.max(1000, next - now + 250);
    window.setTimeout(() => {
      if (wrap._pmnFeaturedAC !== ac) return;
      initFeaturedShowcase(root);
    }, delay);
  }

  const onReady = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const isClickInside = (el, evt) => {
    const t = evt.target;
    return el === t || (t && el.contains(t));
  };

  // ========== MEGA MENU ==========
  function initMegaMenu(root) {
    const mega = qs('[data-mega]', root);
    if (!mega) return;

    const triggers = qsa('[data-mega-trigger]', mega);
    const panelsWrap = qs('.pmn-megaPanels', mega);
    const panels = qsa('[data-mega-panel]', mega);

    if (!panelsWrap || triggers.length === 0 || panels.length === 0) return;

    let openIndex = null;

    const closeAll = () => {
      openIndex = null;
      panelsWrap.setAttribute('aria-hidden', 'true');
      triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      panels.forEach((p) => (p.hidden = true));
      mega.classList.remove('is-open');
    };

    const openAt = (idx) => {
      if (idx === null || idx === undefined) return closeAll();

      openIndex = idx;
      panelsWrap.setAttribute('aria-hidden', 'false');
      mega.classList.add('is-open');

      triggers.forEach((btn) => {
        const btnIdx = Number(btn.getAttribute('data-mega-trigger'));
        btn.setAttribute('aria-expanded', String(btnIdx === idx));
      });

      panels.forEach((p) => {
        const pIdx = Number(p.getAttribute('data-mega-panel'));
        p.hidden = pIdx !== idx;
      });
    };

    triggers.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = Number(btn.getAttribute('data-mega-trigger'));
        if (Number.isNaN(idx)) return;
        if (openIndex === idx) closeAll();
        else openAt(idx);
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    });

    document.addEventListener('click', (evt) => {
      if (!mega.classList.contains('is-open')) return;
      if (!isClickInside(mega, evt)) closeAll();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mega.classList.contains('is-open')) closeAll();
    });

    const active = triggers.find((b) => b.getAttribute('data-active') === 'true');
    if (active) {
      const idx = Number(active.getAttribute('data-mega-trigger'));
      if (!Number.isNaN(idx)) openAt(idx);
    } else {
      closeAll();
    }
  }

  // ========== CHIPS ROTATION / LIMIT ==========
  function initChips(root) {
    const chipsWrap = qs('.pmn-chips', root);
    if (!chipsWrap) return;

    const auto = (chipsWrap.getAttribute('data-auto') || '').toLowerCase() === 'true';
    const days = Number(chipsWrap.getAttribute('data-days') || '3');
    const count = Number(chipsWrap.getAttribute('data-count') || '10');

    const chips = qsa('.pmn-chip', chipsWrap);
    if (chips.length === 0) return;

    // Limit visible chips
    if (Number.isFinite(count) && count > 0 && chips.length > count) {
      chips.forEach((c, i) => {
        if (i >= count) c.hidden = true;
      });
    }

    if (!auto) return;

    const bucketSize = Number.isFinite(days) && days > 0 ? days : 3;
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    const dayOfYear = Math.floor((now - start) / 86400000);
    const bucket = Math.floor(dayOfYear / bucketSize);

    const storageKey = `pmn_chips_order_${bucket}`;

    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const serialize = (arr) =>
      arr.map((el) => el.getAttribute('data-q') || el.textContent.trim()).join('|');

    let order = null;
    try {
      order = localStorage.getItem(storageKey);
    } catch (_) {
      order = null;
    }

    const activeChips = chips.filter((c) => !c.hidden);
    let arranged = activeChips.slice();

    if (order) {
      const wanted = order.split('|');
      arranged.sort((a, b) => {
        const aq = a.getAttribute('data-q') || a.textContent.trim();
        const bq = b.getAttribute('data-q') || b.textContent.trim();
        const ai = wanted.indexOf(aq);
        const bi = wanted.indexOf(bq);
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
      });
    } else {
      arranged = shuffle(arranged);
      try {
        localStorage.setItem(storageKey, serialize(arranged));
      } catch (_) {}
    }

    arranged.forEach((chip) => chipsWrap.appendChild(chip));
  }

  // ========== SEARCH SUGGEST (SHOPIFY PREDICTIVE SEARCH) ==========
  function initSuggest(root) {
    const hero = qs('.pmn-searchHero', root);
    if (!hero) return;

    const input = qs('.pmn-searchInput[name="q"]', hero);
    const suggest = qs('.pmn-suggest', hero);
    const inner = qs('.pmn-suggestInner', hero);

    if (!input || !suggest || !inner) return;

    const currency = root.getAttribute('data-currency') || '';

    const escapeHtml = (s) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const money = (val) => {
      if (val === null || val === undefined) return '';

      const raw = String(val).trim();
      if (!raw) return '';

      // If the API returns a decimal string (e.g. "52.00" or "52,00"), it is already in major units.
      const hasDecimal = /[.,]/.test(raw);

      let amountMajor = 0;
      if (hasDecimal) {
        const n = Number(raw.replace(',', '.'));
        if (!Number.isFinite(n)) return '';
        amountMajor = n;
      } else {
        // Otherwise assume it's cents (minor units)
        const n = Number(raw);
        if (!Number.isFinite(n)) return '';
        amountMajor = n / 100;
      }

      const v = amountMajor.toFixed(2).replace('.', ',');
      return currency ? `${v} ${currency}` : `${v} €`;
    };

    let aborter = null;
    let lastQ = '';

    const renderFallback = () => {
      const chipLinks = qsa('.pmn-chip', root)
        .filter((c) => !c.hidden)
        .slice(0, 8)
        .map((c) => {
          const label = (c.textContent || '').trim();
          const href = c.getAttribute('href') || '#';
          return `<a class="pmn-suggestItem" href="${href}">${escapeHtml(label)}</a>`;
        })
        .join('');

      inner.innerHTML = `
        <div class="pmn-suggestTitle">Suggerimenti rapidi</div>
        <div class="pmn-suggestGrid">${chipLinks}</div>
      `;
      suggest.hidden = false;
    };

    const renderProducts = (q, products = [], queries = []) => {
      const items = products.slice(0, 6).map((p) => {
        const title = escapeHtml(p.title || '');
        const url = p.url || '#';
        const vendor = escapeHtml(p.vendor || '');
        const img = p.featured_image && p.featured_image.url ? p.featured_image.url : '';
        const price = money(p.price);
        const compare = p.compare_at_price && p.compare_at_price > p.price ? money(p.compare_at_price) : '';

        return `
          <a class="pmn-psItem" href="${url}">
            <div class="pmn-psImg">${img ? `<img src="${img}" alt="${title}" loading="lazy" width="72" height="72">` : ''}</div>
            <div class="pmn-psBody">
              <div class="pmn-psTitle">${title}</div>
              <div class="pmn-psMeta">${vendor}</div>
              <div class="pmn-psPrice">${price}${compare ? ` <span class="pmn-psCompare">${compare}</span>` : ''}</div>
            </div>
          </a>
        `;
      }).join('');

      const qLinks = (queries || []).slice(0, 6).map((s) => {
        const qq = escapeHtml(s);
        return `<a class="pmn-suggestItem" href="/search?q=${encodeURIComponent(s)}">${qq}</a>`;
      }).join('');

      inner.innerHTML = `
        <div class="pmn-psWrap">
          <div class="pmn-psCol">
            <div class="pmn-suggestTitle">Prodotti</div>
            <div class="pmn-psList">${items || '<div class="pmn-small">Nessun prodotto trovato.</div>'}</div>
            <a class="pmn-psAll" href="/search?q=${encodeURIComponent(q)}">Vedi tutti i risultati →</a>
          </div>
          <div class="pmn-psCol pmn-psCol--right">
            <div class="pmn-suggestTitle">Ricerche correlate</div>
            <div class="pmn-suggestGrid">${qLinks || '<div class="pmn-small">Prova con un termine più specifico.</div>'}</div>
          </div>
        </div>
      `;
      suggest.hidden = false;
    };

    const debounced = (() => {
      let t = null;
      return (fn, wait = 160) => {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    })();

    const fetchSuggest = (val) => {
      const q = (val || '').trim();
      lastQ = q;

      if (q.length < 2) {
        suggest.hidden = true;
        inner.innerHTML = '';
        return;
      }

      // Cancel previous request
      try { if (aborter) aborter.abort(); } catch (_) {}
      aborter = new AbortController();

      // Shopify Predictive Search endpoint
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,vendor,variants.sku`;

      fetch(url, { signal: aborter.signal, headers: { 'Accept': 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error('bad_status');
          return r.json();
        })
        .then((data) => {
          if (q !== lastQ) return;
          const res = data && data.resources ? data.resources : null;
          const products = res && res.results && res.results.products ? res.results.products : [];

          // Build some related queries from chips that include the typed substring
          const related = qsa('.pmn-chip', root)
            .map((c) => (c.getAttribute('data-q') || c.textContent || '').trim())
            .filter((s) => s && s.toLowerCase().includes(q.toLowerCase()))
            .slice(0, 6);

          if (products && products.length) {
            renderProducts(q, products, related);
          } else {
            // If no products, still show related chips, otherwise fallback chips
            if (related.length) renderProducts(q, [], related);
            else renderFallback();
          }
        })
        .catch(() => {
          // Fallback to chips if endpoint not available / blocked
          if (q !== lastQ) return;
          renderFallback();
        });
    };

    const onInput = () => {
      debounced(() => fetchSuggest(input.value), 180);
    };

    input.addEventListener('input', onInput);
    input.addEventListener('focus', onInput);

    document.addEventListener('click', (evt) => {
      if (suggest.hidden) return;
      if (isClickInside(hero, evt)) return;
      suggest.hidden = true;
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !suggest.hidden) suggest.hidden = true;
    });
  }

  // ========== SHIPPING COUNTDOWN (CUT-OFF) ==========
  function initShippingCountdown(root) {
    const box = qs('[data-ship-countdown]', root);
    if (!box) return;

    const cutoff = (box.getAttribute('data-cutoff') || '16:40').trim();
    const tz = (box.getAttribute('data-tz') || 'Europe/Rome').trim();
    const business = (box.getAttribute('data-business-days') || '1,2,3,4,5')
      .split(',')
      .map((x) => Number(String(x).trim()))
      .filter((n) => Number.isFinite(n));

    const label = qs('.pmn-cutoffLabel', box);
    if (label) label.textContent = cutoff;

    const sub = qs('.pmn-shipSub', box);

    const elH = qs('[data-val="hours"]', box);
    const elM = qs('[data-val="minutes"]', box);
    const elS = qs('[data-val="seconds"]', box);

    const ringH = qs('[data-ring="hours"]', box);
    const ringM = qs('[data-ring="minutes"]', box);
    const ringS = qs('[data-ring="seconds"]', box);

    const CIRC = 283;
    const pad2 = (n) => String(n).padStart(2, '0');

    const getNowParts = () => {
      const d = new Date();
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = fmt.formatToParts(d);
      const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
      return { y: get('year'), mo: get('month'), da: get('day'), hh: get('hour'), mm: get('minute'), ss: get('second') };
    };

    // Date representing y-mo-da hh:mm:ss in timezone tz
    const makeZonedDate = (y, mo, da, hh, mm, ss) => {
      const utc = new Date(Date.UTC(y, mo - 1, da, hh, mm, ss || 0));
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = fmt.formatToParts(utc);
      const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);

      const zy = get('year');
      const zmo = get('month');
      const zda = get('day');
      const zhh = get('hour');
      const zmm = get('minute');
      const zss = get('second');

      const desiredUTC = new Date(Date.UTC(y, mo - 1, da, hh, mm, ss || 0));
      const currentZonedAsUTC = new Date(Date.UTC(zy, zmo - 1, zda, zhh, zmm, zss));
      const diff = desiredUTC.getTime() - currentZonedAsUTC.getTime();
      return new Date(utc.getTime() + diff);
    };

    const parseCutoff = () => {
      const m = cutoff.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return { ch: 16, cm: 40 };
      return {
        ch: Math.min(23, Math.max(0, Number(m[1]))),
        cm: Math.min(59, Math.max(0, Number(m[2])))
      };
    };

    const weekdayInTZ = (y, mo, da) => {
      const d = makeZonedDate(y, mo, da, 12, 0, 0);
      const wk = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d);
      const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return map[wk] ?? 0;
    };

    const nextBusinessDate = (y, mo, da) => {
      for (let i = 0; i < 20; i++) {
        const wd = weekdayInTZ(y, mo, da);
        if (business.includes(wd)) return { y, mo, da };

        const d = makeZonedDate(y, mo, da, 12, 0, 0);
        d.setUTCDate(d.getUTCDate() + 1);
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
        const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
        y = get('year');
        mo = get('month');
        da = get('day');
      }
      return { y, mo, da };
    };

    const { ch, cm } = parseCutoff();

    const computeTarget = () => {
      const nowP = getNowParts();

      // If today is not business day -> move to next business day
      let base = nextBusinessDate(nowP.y, nowP.mo, nowP.da);
      let target = makeZonedDate(base.y, base.mo, base.da, ch, cm, 0);

      // If already past cutoff -> next business day
      const now = makeZonedDate(nowP.y, nowP.mo, nowP.da, nowP.hh, nowP.mm, nowP.ss);
      if (now.getTime() >= target.getTime()) {
        const tmp = makeZonedDate(base.y, base.mo, base.da, 12, 0, 0);
        tmp.setUTCDate(tmp.getUTCDate() + 1);
        const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(tmp);
        const get = (t) => Number(p.find((x) => x.type === t)?.value || 0);
        base = nextBusinessDate(get('year'), get('month'), get('day'));
        target = makeZonedDate(base.y, base.mo, base.da, ch, cm, 0);
      }

      if (sub) {
        const todayKey = `${nowP.y}-${pad2(nowP.mo)}-${pad2(nowP.da)}`;
        const tgtParts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(target);
        const get2 = (t) => Number(tgtParts.find((x) => x.type === t)?.value || 0);
        const tgtKey = `${get2('year')}-${pad2(get2('month'))}-${pad2(get2('day'))}`;
        sub.textContent = (tgtKey === todayKey) ? 'Spedito oggi.' : 'Spedito il prossimo giorno lavorativo.';
      }

      return target;
    };

    let target = computeTarget();

    const tick = () => {
      const nowP = getNowParts();
      const now = makeZonedDate(nowP.y, nowP.mo, nowP.da, nowP.hh, nowP.mm, nowP.ss);

      if (now.getTime() >= target.getTime()) target = computeTarget();

      const diffMs = Math.max(0, target.getTime() - now.getTime());
      const totalSec = Math.floor(diffMs / 1000);

      const hours = Math.floor(totalSec / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      if (elH) elH.textContent = String(hours);
      if (elM) elM.textContent = pad2(minutes);
      if (elS) elS.textContent = pad2(seconds);

      const hFrac = 1 - Math.min(1, hours / 24);
      const mFrac = 1 - Math.min(1, minutes / 60);
      const sFrac = 1 - Math.min(1, seconds / 60);

      if (ringH) ringH.setAttribute('stroke-dasharray', `${Math.round(CIRC * hFrac)} ${CIRC}`);
      if (ringM) ringM.setAttribute('stroke-dasharray', `${Math.round(CIRC * mFrac)} ${CIRC}`);
      if (ringS) ringS.setAttribute('stroke-dasharray', `${Math.round(CIRC * sFrac)} ${CIRC}`);
    };

    tick();
    const t = setInterval(tick, 1000);

    // Cleanup if removed from DOM
    const obs = new MutationObserver(() => {
      if (!document.body.contains(box)) {
        clearInterval(t);
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ========== FEATURED PRODUCT SLIDER (ROTATES 6 EVERY X HOURS) ==========
  function initProductSlider(root) {
    const slider = qs('[data-prod-slider]', root);
    if (!slider) return;

    const track = qs('.pmn-prodTrack', slider);
    if (!track) return;

    const slides = qsa('.pmn-prodSlide', track);
    if (slides.length === 0) return;

    slides.forEach((el) => { el.hidden = false; });

    const rotateHours = Number(slider.getAttribute('data-rotate-hours') || '5');
    const pageSize = Number(slider.getAttribute('data-page-size') || '6');

    const hours = Number.isFinite(rotateHours) && rotateHours > 0 ? rotateHours : 5;
    const size = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 6;

    const now = Date.now();
    const bucketMs = hours * 60 * 60 * 1000;
    const bucket = Math.floor(now / bucketMs);

    const sectionId = root.getAttribute('data-section-id') || '';
    const storageKey = `pmn_featured_${sectionId}_${hours}h_${size}_${bucket}`;

    const shuffle = (arr) => {
      const out = arr.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    };

    const applySelection = (indices) => {
      if (slides.length <= size) {
        slides.forEach((el) => { el.hidden = false; });
      } else {
        const valid = indices.filter((i) => Number.isInteger(i) && i >= 0 && i < slides.length);
        const uniq = [];
        const seen = new Set();

        for (const i of valid) {
          if (seen.has(i)) continue;
          seen.add(i);
          uniq.push(i);
        }

        if (uniq.length < size) {
          for (let i = 0; i < slides.length && uniq.length < size; i++) {
            if (!seen.has(i)) {
              seen.add(i);
              uniq.push(i);
            }
          }
        }

        const set = new Set(uniq.slice(0, size));
        slides.forEach((el, idx) => { el.hidden = !set.has(idx); });
      }

      slider.classList.remove('is-fading');
      void slider.offsetWidth;
      slider.classList.add('is-fading');
    };

    let selection = null;
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) selection = parsed;
      }
    } catch (_) {}

    if (!selection) {
      const indices = slides.map((_, i) => i);
      selection = shuffle(indices).slice(0, Math.min(size, indices.length));
      try { localStorage.setItem(storageKey, JSON.stringify(selection)); } catch (_) {}
    } else if (slides.length > size && selection.length < size) {
      const indices = slides.map((_, i) => i);
      const mixed = shuffle(indices);
      const seen = new Set(selection);
      for (const idx of mixed) {
        if (selection.length >= size) break;
        if (!seen.has(idx)) {
          seen.add(idx);
          selection.push(idx);
        }
      }
      try { localStorage.setItem(storageKey, JSON.stringify(selection)); } catch (_) {}
    }

    applySelection(selection);
  }

  // ========== CATEGORY CAPO PREVIEW (IMAGE + TITLE) ==========
  function initCategoryCapo(root) {
    const capo = qs('[data-cat-capo]', root);
    const catsWrap = qs('.pmn-cats', root);
    if (!capo || !catsWrap) return;

    const links = qsa('.pmn-cat', catsWrap);
    if (links.length === 0) return;

    const capoLink = qs('.pmn-catCapoLink', capo);
    const capoBg = qs('[data-capo-bg]', capo);
    const capoTitle = qs('[data-capo-title]', capo);

    const setFromLink = (a) => {
      if (!a) return;

      const title =
        a.getAttribute('data-title') ||
        (qs('b', a) ? qs('b', a).textContent.trim() : a.textContent.trim());

      const img = a.getAttribute('data-img') || '';
      const href = a.getAttribute('href') || '#';

      if (capoTitle) capoTitle.textContent = title;
      if (capoLink) capoLink.setAttribute('href', href);

      if (capoBg) {
        if (img) {
          capoBg.style.backgroundImage = `url(${img})`;
          capoBg.classList.add('has-img');
        } else {
          capoBg.style.backgroundImage = '';
          capoBg.classList.remove('has-img');
        }
      }

      links.forEach((el) => el.classList.toggle('is-capo', el === a));
      capo.hidden = false;
    };

    // Default: first category
    setFromLink(links[0]);

    // Hover/focus updates
    links.forEach((a) => {
      a.addEventListener('mouseenter', () => setFromLink(a));
      a.addEventListener('focus', () => setFromLink(a));
    });

    // Touch: first tap previews, second tap follows
    const isTouch = window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (isTouch) {
      links.forEach((a) => {
        a.addEventListener('click', (e) => {
          if (!a.classList.contains('is-capo')) {
            e.preventDefault();
            setFromLink(a);
          }
        });
      });
    }
  }

  // ========== NEWSLETTER CTA BRIDGE (uses existing footer form) ==========
  function initNewsletterCtaBridge() {
    const btn = document.getElementById('pmn-newsletterBtn');
    if (!btn) return;

    btn.addEventListener('click', () => {
      const footer = document.querySelector('footer');
      if (!footer) return;

      const footerForm =
        footer.querySelector('form.t4s-newsletter__form') ||
        footer.querySelector('form[action*="newsletter"]') ||
        footer.querySelector('form[action*="contact"]') ||
        footer.querySelector('form');

      const footerEmail =
        footer.querySelector('input.t4s-newsletter__email') ||
        footer.querySelector('input[type="email"]') ||
        footer.querySelector('input[name*="email"]');

      const target = footerEmail || footerForm || footer;
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {}

      if (footerEmail) {
        setTimeout(() => {
          try {
            footerEmail.focus({ preventScroll: true });
            if (typeof footerEmail.select === 'function') footerEmail.select();
          } catch (_) {}
        }, 250);
      }

      // If email already filled, attempt submit
      if (footerForm && footerEmail && footerEmail.value && footerEmail.value.includes('@')) {
        const submit = footerForm.querySelector('button[type="submit"], input[type="submit"]');
        if (submit) submit.click();
        else if (typeof footerForm.submit === 'function') footerForm.submit();
      }
    });
  }

  // ========== LEAD FORM (Google Sheets via Apps Script endpoint) ==========
  function initLeadForm(root) {
    const form = qs('[data-lead-form]', root);
    if (!form) return;

    const msg = qs('[data-lead-msg]', form);
    const btn = qs('button[type="submit"]', form);

    const getSettingText = (key, fallback) => {
      // allow messages to be passed via data attributes on the form
      const v = form.getAttribute(`data-${key}`);
      return (v && v.trim()) ? v.trim() : fallback;
    };

    const SUCCESS_TXT = getSettingText('success', 'Richiesta inviata! Ti ricontattiamo entro 24h lavorative.');
    const ERROR_TXT = getSettingText('error', 'Ops, qualcosa non ha funzionato. Riprova tra poco oppure scrivici su WhatsApp.');

    // Fill UTM hidden inputs
    try {
      const params = new URLSearchParams(window.location.search);
      const map = { source: 'utm_source', medium: 'utm_medium', campaign: 'utm_campaign', content: 'utm_content', term: 'utm_term' };
      Object.keys(map).forEach((k) => {
        const el = qs(`[data-utm="${k}"]`, form);
        if (el) el.value = params.get(map[k]) || '';
      });
    } catch (_) {}

    const setMsg = (t, ok) => {
      if (!msg) return;
      msg.textContent = t;
      msg.classList.toggle('is-ok', !!ok);
      msg.classList.toggle('is-err', !ok);
    };

    const setLoading = (on) => {
      if (!btn) return;
      btn.disabled = !!on;
      btn.classList.toggle('is-loading', !!on);
    };

    const postForm = async (endpoint, fd) => {
      // Try normal CORS first
      try {
        const r = await fetch(endpoint, { method: 'POST', body: fd, mode: 'cors' });
        if (r.ok) return true;
        // Some Apps Script deployments respond with 302/0; treat non-OK as failure here
        return false;
      } catch (_) {
        // Fallback: no-cors (opaque) -> assume success if request dispatched
        try {
          await fetch(endpoint, { method: 'POST', body: fd, mode: 'no-cors' });
          return true;
        } catch (e2) {
          return false;
        }
      }
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const endpoint = String(form.getAttribute('action') || '').trim();
      if (!endpoint || endpoint.includes('example.com')) {
        setMsg('Endpoint non configurato: inserisci URL Apps Script nelle impostazioni della sezione.', false);
        return;
      }

      // honeypot anti-spam
      const hp = qs('input[name="company_site"]', form);
      if (hp && hp.value && hp.value.trim()) return;

      if (!form.checkValidity()) {
        form.reportValidity?.();
        return;
      }

      setLoading(true);
      setMsg('Invio in corso…', true);

      const fd = new FormData(form);
      const ok = await postForm(endpoint, fd);

      if (ok) {
        form.reset();
        setMsg(SUCCESS_TXT, true);
      } else {
        setMsg(ERROR_TXT, false);
      }

      setLoading(false);
    });
  }

  onReady(() => {
    const roots = qsa('.pmn-landing');
    if (roots.length === 0) return;

    roots.forEach((root) => {
      initMegaMenu(root);
      initChips(root);
      initSuggest(root);
      initShippingCountdown(root);
      initFeaturedShowcase(root);
      initProductSlider(root);
      initCategoryCapo(root);
      initLeadForm(root);
    });

    initNewsletterCtaBridge();
  });
})();